import MerchantLogoPlaceholder from '@cohort/components-xps/components/MerchantLogoPlaceholder';
import CohortLogo from '@cohort/merchants/components/CohortLogo';
import {SelectPicker} from '@cohort/merchants/components/form/v2/SelectPicker';
import TestModeToggle from '@cohort/merchants/components/test-mode/TestModeToggle';
import type FeatureFlags from '@cohort/merchants/featureFlags';
import {useOrganizations} from '@cohort/merchants/hooks/api/Organizations';
import {useTestModeStore} from '@cohort/merchants/hooks/stores/testMode';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {logout} from '@cohort/merchants/lib/Firebase';
import {
  getAppsRoute,
  getBrandRoute,
  getCampaignsRoute,
  getCohortsRoute,
  getDigitalAssetsRoute,
  getIntegrationsSettingsRoute,
  getLanguagesRoute,
  getMembershipPassConfigurationRoute,
  getNotificationsSettingsRoute,
  getPaymentSettingsRoute,
  getPerksRoute,
  getQrReadersSettingsRoute,
  getTeamRoute,
  getUserPropertiesSettingsRoute,
  getUsersRoute,
} from '@cohort/merchants/lib/Pages';
import {
  trackChangeOrganizationButtonClicked,
  trackNavbarLinkButtonClicked,
} from '@cohort/merchants/lib/Tracking';
import {getOrganizationSigninUrl} from '@cohort/merchants/lib/Utils';
import {getImageUrl, Sizes} from '@cohort/shared/utils/media';
import {cn} from '@cohort/shared-frontend/utils/classNames';
import {Disclosure, Menu, Transition} from '@headlessui/react';
import {
  Bell,
  CaretDown,
  Check,
  Code,
  CreditCard,
  Database,
  Desktop,
  GearSix,
  IdentificationCard,
  List,
  QrCode,
  SignOut,
  Swatches,
  Translate,
  User,
  Users,
  X,
} from '@phosphor-icons/react';
import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useLocation} from 'react-router-dom';
import {shallow} from 'zustand/shallow';

const Navbar: React.FC = () => {
  const location = useLocation();
  const {t} = useTranslation('components', {keyPrefix: 'navBar'});
  const testModeEnabled = useTestModeStore(store => store.testModeEnabled);
  const {featureNotifications} = useFlags<FeatureFlags>();

  const {organizationMember, organization} = useUserSessionStore(
    store => ({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      organizationMember: store.organizationMember!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      organization: store.organization!,
    }),
    shallow
  );

  const {data: organizations} = useOrganizations();

  const SettingsNavigation = [
    {
      name: t('linkExperienceSpace'),
      href: getBrandRoute().path,
      icon: Swatches,
      pageName: getBrandRoute().name,
    },
    {
      name: t('linkMembershipPassConfiguration'),
      href: getMembershipPassConfigurationRoute().path,
      icon: IdentificationCard,
      pageName: getMembershipPassConfigurationRoute().name,
    },
    {
      name: t('linkConnections'),
      href: getAppsRoute().path,
      icon: Desktop,
      pageName: getAppsRoute().name,
    },
    ...(featureNotifications
      ? [
          {
            name: t('linkNotifications'),
            href: getNotificationsSettingsRoute().path,
            icon: Bell,
            pageName: getNotificationsSettingsRoute().name,
          },
        ]
      : []),
    ...(!testModeEnabled
      ? [
          {
            name: t('linkPayments'),
            href: getPaymentSettingsRoute().path,
            icon: CreditCard,
            pageName: getPaymentSettingsRoute().name,
          },
          {
            name: t('linkTeamMembers'),
            href: getTeamRoute().path,
            icon: Users,
            pageName: getTeamRoute().name,
          },
        ]
      : []),
    {
      name: t('linkIntegrations'),
      href: getIntegrationsSettingsRoute().path,
      icon: Code,
      pageName: getIntegrationsSettingsRoute().name,
    },
    {
      name: t('linkQrReaders'),
      href: getQrReadersSettingsRoute().path,
      icon: QrCode,
      pageName: getQrReadersSettingsRoute().name,
    },
    {
      name: t('linkLanguages'),
      href: getLanguagesRoute().path,
      icon: Translate,
      pageName: getLanguagesRoute().name,
    },
    {
      name: t('linkUserProperties'),
      href: getUserPropertiesSettingsRoute().path,
      icon: Database,
      pageName: getUserPropertiesSettingsRoute().name,
    },
  ];
  const PrimaryNavigation = [
    {
      name: t('linkUsers'),
      href: getUsersRoute().path,
      additionalHrefs: [getCohortsRoute().path],
      pageName: getUsersRoute().name,
    },

    {
      name: t('linkCampaigns'),
      href: getCampaignsRoute().path,
      pageName: getCampaignsRoute().name,
    },
    {
      name: t('linkDigitalAssets'),
      href: getDigitalAssetsRoute().path,
      pageName: getDigitalAssetsRoute().name,
    },
    {
      name: t('linkPerks'),
      href: getPerksRoute().path,
      pageName: getPerksRoute().name,
    },
  ];

  async function handleSignOutClick(): Promise<void> {
    await logout();
  }

  function isCurrentPath(hrefs: string | string[]): boolean {
    hrefs = Array.isArray(hrefs) ? hrefs : [hrefs];
    return hrefs.some(href => location.pathname.startsWith(href.split('?')[0] ?? href));
  }

  async function switchToOrganization(slug: string): Promise<void> {
    if (slug === organization.slug) {
      return;
    }
    const url = getOrganizationSigninUrl(slug);
    window.location.assign(url);
  }

  return (
    <div className="w-full border-b bg-slate-50">
      <div className="m-auto max-w-[var(--content-max-width)] px-8">
        <Disclosure as="nav" className="flex w-full">
          {({open}) => (
            <div className="w-full">
              <div className="flex h-16 items-center justify-between">
                <div className="flex items-center justify-center">
                  <Link to="/" onClick={() => trackNavbarLinkButtonClicked('home')}>
                    <CohortLogo />
                  </Link>
                  <div className="-mb-px hidden h-16 gap-6 text-center text-base font-extrabold md:flex">
                    {PrimaryNavigation.map(item => {
                      const isActive =
                        isCurrentPath(item.href.split('?')[0] ?? '') ||
                        (item.additionalHrefs !== undefined && isCurrentPath(item.additionalHrefs));

                      return (
                        <Link
                          className={cn(
                            'flex cursor-pointer items-center border-b-2 text-sm font-medium',
                            isActive
                              ? 'border-b-primary'
                              : 'border-b-transparent text-slate-500 hover:text-slate-700'
                          )}
                          onClick={() => trackNavbarLinkButtonClicked(item.pageName)}
                          key={item.name}
                          to={item.href}
                          replace
                        >
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>

                {/* Desktop UserMenu */}
                <div className="hidden h-full items-center md:flex">
                  <TestModeToggle />
                  <Menu as="div" className="relative">
                    <Fragment>
                      <Menu.Button className="group mr-4 flex items-center gap-2 text-sm">
                        {organization.merchantLogoFileKey !== null ? (
                          <img
                            src={getImageUrl(
                              import.meta.env.COHORT_ENV,
                              organization.merchantLogoFileKey,
                              {
                                w: Sizes.XS,
                                h: Sizes.XS,
                              }
                            )}
                            className="h-6 w-6 rounded-full border"
                          />
                        ) : (
                          <MerchantLogoPlaceholder size={24} merchantName={organization.name} />
                        )}
                        <p
                          className="text-sm font-medium text-slate-500 group-hover:text-slate-700"
                          data-testid="active-org-name"
                        >
                          {organization.name}
                        </p>

                        <CaretDown className="h-4 w-4 text-slate-400 group-hover:text-slate-600" />
                      </Menu.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items
                          className="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-slate-100 overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                          style={{maxHeight: 'calc(100vh - 100px)'}}
                        >
                          <div className="px-4 py-3">
                            <p className="text-sm">{t('labelOrganizations')}</p>
                          </div>
                          <div className="py-1">
                            {organizations !== undefined &&
                              organizations.map(org => (
                                <Menu.Item key={org.id}>
                                  {({active}: {active: boolean}) => (
                                    <div
                                      onClick={async () => {
                                        trackChangeOrganizationButtonClicked(org);
                                        return switchToOrganization(org.slug);
                                      }}
                                      className={cn(
                                        'group flex cursor-pointer items-center justify-between px-4 py-2 text-sm',
                                        active ? 'bg-slate-100' : 'text-slate-700'
                                      )}
                                    >
                                      <div className="flex items-center gap-3">
                                        {org.merchantLogoFileKey !== null ? (
                                          <img
                                            src={getImageUrl(
                                              import.meta.env.COHORT_ENV,
                                              org.merchantLogoFileKey,
                                              {
                                                w: Sizes.XS,
                                                h: Sizes.XS,
                                              }
                                            )}
                                            className="h-6 w-6 rounded-full border"
                                            alt={org.name}
                                          />
                                        ) : (
                                          <MerchantLogoPlaceholder
                                            size={24}
                                            merchantName={org.name}
                                          />
                                        )}
                                        <span>{org.name}</span>
                                      </div>
                                      <div>
                                        {organization.id === org.id && (
                                          <Check className="h-5 w-5 text-primary" />
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </Menu.Item>
                              ))}
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Fragment>
                  </Menu>
                  <Menu as="div" className="relative h-full ">
                    <Fragment>
                      <Menu.Button
                        className={cn(
                          'mr-4 h-full border-b-2 pt-[2px] text-slate-500 hover:text-slate-700',
                          location.pathname.startsWith('/settings')
                            ? 'border-primary text-slate-700'
                            : 'border-b-transparent'
                        )}
                      >
                        <GearSix size={24} />
                      </Menu.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-slate-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <div className="px-4 py-3">
                            <p className="text-sm">{t('labelOrgSettings')}</p>
                          </div>
                          <div className="py-1">
                            {SettingsNavigation.map(item => (
                              <Menu.Item key={item.href}>
                                {({active}: {active: boolean}) => (
                                  <Link
                                    to={item.href}
                                    className={cn(
                                      active ? 'bg-slate-100' : 'text-slate-700',
                                      'group flex items-center px-4 py-2 text-sm'
                                    )}
                                    onClick={() => trackNavbarLinkButtonClicked(item.pageName)}
                                  >
                                    <item.icon
                                      className="mr-3 h-5 w-5 text-slate-400 group-hover:text-slate-500"
                                      aria-hidden="true"
                                    />
                                    {item.name}
                                  </Link>
                                )}
                              </Menu.Item>
                            ))}
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Fragment>
                  </Menu>
                  <Menu as="div" className="relative h-6">
                    <Fragment>
                      <Menu.Button>
                        <User size={24} className="text-slate-500 hover:text-slate-700" />
                      </Menu.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-slate-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <div className="px-4 py-3">
                            <p className="text-sm">{t('labelSignedInAs')}</p>
                            <p className="truncate text-sm font-medium">
                              {organizationMember.email}
                            </p>
                          </div>
                          <div className="py-1">
                            <Menu.Item>
                              {({active}: {active: boolean}) => (
                                <div
                                  onClick={handleSignOutClick}
                                  className={cn(
                                    'group flex items-center px-4 py-2 text-sm text-red-600 hover:cursor-pointer',
                                    active && 'bg-slate-100'
                                  )}
                                >
                                  <SignOut
                                    className="mr-3 h-5 w-5 text-red-600"
                                    aria-hidden="true"
                                  />
                                  {t('labelSignOut')}
                                </div>
                              )}
                            </Menu.Item>
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Fragment>
                  </Menu>
                </div>

                {/* Mobile menu icon */}
                <div className="md:hidden">
                  <Disclosure.Button>
                    {open ? (
                      <X className="h-6 w-6" aria-hidden="true" />
                    ) : (
                      <List className="h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>

              {/* Mobile menu panel */}
              <Disclosure.Panel className="my-6 flex flex-col gap-y-6 text-left md:hidden">
                <Fragment>
                  {[...PrimaryNavigation, ...SettingsNavigation].map(item => (
                    <Disclosure.Button
                      key={item.name}
                      className={cn(
                        'text-left text-black',
                        isCurrentPath(item.href) && 'border-l-2 border-primary pl-2 font-bold'
                      )}
                    >
                      <Link
                        to={item.href}
                        onClick={() => trackNavbarLinkButtonClicked(item.pageName)}
                      >
                        {item.name}
                      </Link>
                    </Disclosure.Button>
                  ))}
                  {organizations !== undefined && (
                    <div className="mt-3 w-full">
                      <SelectPicker
                        label={t('labelChangeOrganization')}
                        options={organizations.map(org => {
                          return {label: org.name, value: org.slug};
                        })}
                        value={{value: organization.slug, label: organization.name}}
                        onChange={async org => {
                          trackChangeOrganizationButtonClicked(organization);
                          if (org !== null) {
                            switchToOrganization(org.value);
                          }
                        }}
                        name="change-organization"
                      />
                    </div>
                  )}
                  <Disclosure.Button
                    className="w-full rounded-md border bg-white p-2 text-red-600 shadow-sm"
                    onClick={handleSignOutClick}
                  >
                    {t('labelSignOut')}
                  </Disclosure.Button>
                </Fragment>
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>
      </div>
    </div>
  );
};

export default Navbar;
